import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Menu,
  X,
  Github,
  Linkedin,
  Download,
  ArrowUpRight,
} from "lucide-react";

/*
|--------------------------------------------------------------------------
| Navigation Links
|--------------------------------------------------------------------------
| Keep this outside the component so the reference remains stable.
| This also prevents the React Hook dependency warning.
*/
const navLinks = [
  { name: "Home", id: "home" },
  { name: "About", id: "about" },
  { name: "Skills", id: "skills" },
  { name: "Experience", id: "experience" },
  { name: "Projects", id: "projects" },
  { name: "Contact", id: "contact" },
];

const RESUME_VIEW =
  "https://drive.google.com/file/d/1JST-jDldFbOHH6Pt9X1grxwP_wP9LNOt9/view";

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("home");
  const [isScrolled, setIsScrolled] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  /*
  |--------------------------------------------------------------------------
  | Scroll Handler
  |--------------------------------------------------------------------------
  */
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;

      /*
      |--------------------------------------------------------------------------
      | Navbar background state
      |--------------------------------------------------------------------------
      */
      setIsScrolled(scrollY > 30);

      /*
      |--------------------------------------------------------------------------
      | Scroll progress
      |--------------------------------------------------------------------------
      */
      const totalHeight =
        document.documentElement.scrollHeight - window.innerHeight;

      const progress = totalHeight > 0 ? (scrollY / totalHeight) * 100 : 0;

      setScrollProgress(Math.min(progress, 100));

      /*
      |--------------------------------------------------------------------------
      | Active section
      |--------------------------------------------------------------------------
      */
      const sections = navLinks
        .map((link) => document.getElementById(link.id))
        .filter(Boolean);

      let currentSection = "home";

      sections.forEach((section) => {
        const sectionTop = section.offsetTop - 180;

        if (scrollY >= sectionTop) {
          currentSection = section.id;
        }
      });

      setActiveSection(currentSection);
    };

    window.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    /*
    |--------------------------------------------------------------------------
    | Initial calculation
    |--------------------------------------------------------------------------
    */
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  /*
  |--------------------------------------------------------------------------
  | Scroll To Section
  |--------------------------------------------------------------------------
  */
  const scrollToSection = (id) => {
    setMenuOpen(false);

    const section = document.getElementById(id);

    if (!section) return;

    const offset = 90;

    const position =
      section.getBoundingClientRect().top + window.scrollY - offset;

    window.scrollTo({
      top: position,
      behavior: "smooth",
    });
  };

  return (
    <>
      {/* ================================================================
          Scroll Progress
      ================================================================= */}
      <div className="fixed left-0 right-0 top-0 z-[100] h-[2px]">
        <div
          className="h-full bg-gradient-to-r from-orange-500 via-orange-400 to-amber-400 shadow-[0_0_12px_rgba(249,115,22,0.8)]"
          style={{
            width: `${scrollProgress}%`,
            transition: "width 100ms linear",
          }}
        />
      </div>

      {/* ================================================================
          Navbar
      ================================================================= */}
      <nav className="fixed left-0 right-0 top-5 z-50 px-4">
        <motion.div
          initial={{
            opacity: 0,
            y: -25,
          }}
          animate={{
            opacity: 1,
            y: 0,
          }}
          transition={{
            duration: 0.7,
            ease: [0.22, 1, 0.36, 1],
          }}
          className={`
            relative mx-auto
            w-fit
            max-w-[calc(100vw-32px)]
            rounded-2xl
            border
            transition-all duration-500
            ${
              isScrolled
                ? "border-orange-500/15 bg-[#08080b]/90 shadow-2xl shadow-black/40 backdrop-blur-2xl"
                : "border-white/[0.08] bg-black/35 backdrop-blur-xl"
            }
          `}
        >
          {/* Orange glow */}
          <div className="pointer-events-none absolute -inset-1 rounded-2xl bg-orange-500/[0.025] blur-xl" />

          <div className="relative flex items-center gap-1 px-2 py-2">
            {/* ==========================================================
                Logo
            =========================================================== */}
            <button
              onClick={() => scrollToSection("home")}
              aria-label="Go to home"
              className="group flex items-center gap-3 px-3 py-1.5"
            >
              <div
                className="
                  relative
                  flex h-9 w-9
                  items-center justify-center
                  rounded-xl
                  border border-orange-400/20
                  bg-orange-500/10
                  shadow-[0_0_20px_rgba(249,115,22,0.08)]
                  transition-all duration-300
                  group-hover:border-orange-400/40
                  group-hover:bg-orange-500/15
                  group-hover:shadow-[0_0_25px_rgba(249,115,22,0.18)]
                "
              >
                <span className="text-xs font-black tracking-tight text-orange-400">
                  AP
                </span>
              </div>

              <div className="hidden text-left sm:block">
                <p className="text-sm font-bold tracking-tight text-gray-100">
                  Arun Pungle
                </p>

                <p className="text-[9px] uppercase tracking-[0.18em] text-gray-600">
                  Backend Developer
                </p>
              </div>
            </button>

            {/* ==========================================================
                Divider
            =========================================================== */}
            <div className="mx-2 hidden h-7 w-px bg-white/[0.07] md:block" />

            {/* ==========================================================
                Desktop Navigation
            =========================================================== */}
            <div className="hidden items-center gap-1 md:flex">
              {navLinks.map((link) => {
                const isActive = activeSection === link.id;

                return (
                  <button
                    key={link.id}
                    onClick={() => scrollToSection(link.id)}
                    className={`
                      relative
                      rounded-xl
                      px-4
                      py-2.5
                      text-xs
                      font-medium
                      transition-all
                      duration-300
                      ${
                        isActive
                          ? "text-orange-300"
                          : "text-gray-400 hover:text-gray-100"
                      }
                    `}
                  >
                    {/* Active background */}
                    {isActive && (
                      <motion.div
                        layoutId="navbar-active"
                        transition={{
                          type: "spring",
                          stiffness: 350,
                          damping: 30,
                        }}
                        className="
                          absolute inset-0
                          rounded-xl
                          border border-orange-500/15
                          bg-orange-500/[0.07]
                        "
                      />
                    )}

                    <span className="relative z-10">{link.name}</span>

                    {/* Active dot */}
                    {isActive && (
                      <motion.span
                        layoutId="navbar-dot"
                        className="
                          absolute
                          bottom-1
                          left-1/2
                          h-1
                          w-1
                          -translate-x-1/2
                          rounded-full
                          bg-orange-400
                          shadow-[0_0_8px_rgba(249,115,22,0.9)]
                        "
                      />
                    )}
                  </button>
                );
              })}
            </div>

            {/* ==========================================================
                Right Side
            =========================================================== */}
            <div className="ml-2 hidden items-center gap-2 md:flex">
              {/* GitHub */}
              <a
                href="https://github.com/ArunPungle"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="GitHub"
                className="
                  flex h-9 w-9
                  items-center justify-center
                  rounded-xl
                  border border-white/[0.07]
                  bg-white/[0.02]
                  text-gray-500
                  transition-all duration-300
                  hover:border-orange-500/20
                  hover:bg-orange-500/[0.06]
                  hover:text-orange-400
                "
              >
                <Github size={16} />
              </a>

              {/* LinkedIn */}
              <a
                href="https://www.linkedin.com/in/arun-pungle"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="
                  flex h-9 w-9
                  items-center justify-center
                  rounded-xl
                  border border-white/[0.07]
                  bg-white/[0.02]
                  text-gray-500
                  transition-all duration-300
                  hover:border-orange-500/20
                  hover:bg-orange-500/[0.06]
                  hover:text-orange-400
                "
              >
                <Linkedin size={16} />
              </a>

              {/* Resume */}
              <a
                href={RESUME_VIEW}
                target="_blank"
                rel="noopener noreferrer"
                className="
                  group
                  ml-1
                  flex
                  items-center
                  gap-2
                  rounded-xl
                  border border-orange-500/20
                  bg-orange-500/[0.08]
                  px-4
                  py-2.5
                  text-xs
                  font-semibold
                  text-orange-300
                  transition-all duration-300
                  hover:border-orange-400/40
                  hover:bg-orange-500/15
                  hover:text-orange-200
                  hover:shadow-[0_0_20px_rgba(249,115,22,0.12)]
                "
              >
                <Download size={14} />

                <span>Resume</span>

                <ArrowUpRight
                  size={13}
                  className="
                    transition-transform duration-300
                    group-hover:translate-x-0.5
                    group-hover:-translate-y-0.5
                  "
                />
              </a>
            </div>

            {/* ==========================================================
                Mobile Menu Button
            =========================================================== */}
            <button
              onClick={() => setMenuOpen((prev) => !prev)}
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              aria-expanded={menuOpen}
              className="
                flex h-10 w-10
                items-center justify-center
                rounded-xl
                border border-white/[0.07]
                bg-white/[0.02]
                text-orange-400
                transition-all duration-300
                hover:border-orange-500/20
                hover:bg-orange-500/[0.07]
                md:hidden
              "
            >
              <AnimatePresence mode="wait">
                {menuOpen ? (
                  <motion.div
                    key="close"
                    initial={{
                      rotate: -90,
                      opacity: 0,
                    }}
                    animate={{
                      rotate: 0,
                      opacity: 1,
                    }}
                    exit={{
                      rotate: 90,
                      opacity: 0,
                    }}
                  >
                    <X size={19} />
                  </motion.div>
                ) : (
                  <motion.div
                    key="menu"
                    initial={{
                      rotate: 90,
                      opacity: 0,
                    }}
                    animate={{
                      rotate: 0,
                      opacity: 1,
                    }}
                    exit={{
                      rotate: -90,
                      opacity: 0,
                    }}
                  >
                    <Menu size={19} />
                  </motion.div>
                )}
              </AnimatePresence>
            </button>
          </div>
        </motion.div>

        {/* ================================================================
            Mobile Menu
        ================================================================= */}
        <AnimatePresence>
          {menuOpen && (
            <motion.div
              initial={{
                opacity: 0,
                y: -10,
                scale: 0.96,
              }}
              animate={{
                opacity: 1,
                y: 0,
                scale: 1,
              }}
              exit={{
                opacity: 0,
                y: -10,
                scale: 0.96,
              }}
              transition={{
                duration: 0.25,
              }}
              className="
                mx-auto
                mt-3
                w-[calc(100%-20px)]
                max-w-[340px]
                rounded-2xl
                border border-white/[0.08]
                bg-[#09090c]/95
                p-3
                shadow-2xl
                shadow-black/50
                backdrop-blur-2xl
                md:hidden
              "
            >
              {/* Mobile Navigation */}
              <div className="space-y-1">
                {navLinks.map((link, index) => {
                  const isActive = activeSection === link.id;

                  return (
                    <motion.button
                      key={link.id}
                      initial={{
                        opacity: 0,
                        x: -10,
                      }}
                      animate={{
                        opacity: 1,
                        x: 0,
                      }}
                      transition={{
                        delay: index * 0.04,
                      }}
                      onClick={() => scrollToSection(link.id)}
                      className={`
                        w-full
                        rounded-xl
                        px-4
                        py-3
                        text-left
                        text-sm
                        font-medium
                        transition-all duration-300
                        ${
                          isActive
                            ? "border border-orange-500/10 bg-orange-500/[0.07] text-orange-300"
                            : "text-gray-400 hover:bg-white/[0.03] hover:text-white"
                        }
                      `}
                    >
                      <div className="flex items-center justify-between">
                        <span>{link.name}</span>

                        {isActive && (
                          <span
                            className="
                              h-1.5 w-1.5
                              rounded-full
                              bg-orange-400
                              shadow-[0_0_8px_rgba(249,115,22,0.8)]
                            "
                          />
                        )}
                      </div>
                    </motion.button>
                  );
                })}
              </div>

              {/* Mobile Resume */}
              <div className="mt-3 border-t border-white/[0.06] pt-3">
                <a
                  href={RESUME_VIEW}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="
                    flex
                    w-full
                    items-center
                    justify-center
                    gap-2
                    rounded-xl
                    border border-orange-500/20
                    bg-orange-500/[0.08]
                    px-4
                    py-3
                    text-sm
                    font-semibold
                    text-orange-300
                    transition
                    hover:bg-orange-500/[0.14]
                  "
                >
                  <Download size={16} />
                  View Resume
                </a>
              </div>

              {/* Mobile Social */}
              <div className="mt-3 flex justify-center gap-2">
                <a
                  href="https://github.com/ArunPungle"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="GitHub"
                  className="
                    flex h-10 w-10
                    items-center justify-center
                    rounded-xl
                    border border-white/[0.07]
                    bg-white/[0.02]
                    text-gray-500
                    transition
                    hover:border-orange-500/20
                    hover:text-orange-400
                  "
                >
                  <Github size={17} />
                </a>

                <a
                  href="https://www.linkedin.com/in/arun-pungle"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn"
                  className="
                    flex h-10 w-10
                    items-center justify-center
                    rounded-xl
                    border border-white/[0.07]
                    bg-white/[0.02]
                    text-gray-500
                    transition
                    hover:border-orange-500/20
                    hover:text-orange-400
                  "
                >
                  <Linkedin size={17} />
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </>
  );
};

export default Navbar;
